import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Quick-add upsell component for the cart page.
 *
 * Adds the clicked variant to the cart and dispatches a CartAddEvent with the
 * re-rendered cart sections, so cart-items-component morphs the page in place
 * (items, totals and the upsell list itself).
 */
const AUTO_SCROLL_INTERVAL = 3500;

class CartUpsellComponent extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #autoScrollTimer = null;
  #paused = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#startAutoScroll();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#stopAutoScroll();
  }

  get #list() {
    return this.querySelector('[data-upsell-list]');
  }

  #startAutoScroll() {
    const list = this.#list;
    if (!(list instanceof HTMLElement)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Pausa o avanço automático enquanto o usuário interage.
    list.addEventListener('pointerenter', this.#pause);
    list.addEventListener('pointerleave', this.#resume);
    list.addEventListener('pointerdown', this.#pause);
    list.addEventListener('focusin', this.#pause);
    list.addEventListener('focusout', this.#resume);

    this.#autoScrollTimer = setInterval(this.#tick, AUTO_SCROLL_INTERVAL);
  }

  #stopAutoScroll() {
    if (this.#autoScrollTimer != null) {
      clearInterval(this.#autoScrollTimer);
      this.#autoScrollTimer = null;
    }
  }

  #pause = () => {
    this.#paused = true;
  };

  #resume = () => {
    this.#paused = false;
  };

  #tick = () => {
    const list = this.#list;
    if (this.#paused || !(list instanceof HTMLElement)) return;

    const maxScroll = list.scrollWidth - list.clientWidth;
    if (maxScroll <= 1) return; // nada para rolar

    if (list.scrollLeft >= maxScroll - 1) {
      list.scrollTo({ left: 0 });
    } else {
      // Avança uma "página" (a largura visível ≈ uma coluna).
      list.scrollBy({ left: list.clientWidth });
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button[data-variant-id]');
    if (!(button instanceof HTMLButtonElement) || button.hasAttribute('aria-busy')) return;

    this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.add(el.dataset.sectionId);
      }
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          // [PB 19-09] Propriedade oculta (_) marca a linha vinda do card: mede o cross-sell no pedido.
          // [Claude 24-10 v39] O card do organizador manda data-origem="cross-sell-org" (mede a adesão separada).
          items: [{ id: variantId, quantity: 1, properties: { _origem: button.dataset.origem || 'cross-sell' } }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'cart-upsell-component',
          itemCount: 1,
          variantId: String(variantId),
          sections: data.sections,
        })
      );
    } catch {
      // Network failure: keep the page usable; the button is re-enabled below.
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
